// components/AdvancedOpportunityFilterModal.jsx
import React, { useState, useEffect } from "react";
import { X, Filter } from "lucide-react";
import AdvancedOpportunityFilterBuilder from "./AdvancedOpportunityFilterBuilder";
import FilterPresetSelector from "./FilterPresetSelector";
import SaveFilterPresetModal from "./SaveFilterPresetModal";
import apiService from "../services/api";
import { useTheme } from "../contexts/ThemeContext";

const AdvancedOpportunityFilterModal = ({
  isOpen,
  onClose,
  onApplyFilters,
}) => {
  const { isDarkMode } = useTheme();

  const [filterConfig, setFilterConfig] = useState({
    logic: "AND",
    conditions: [],
  });
  const [presets, setPresets] = useState([]);
  const [selectedPresetId, setSelectedPresetId] = useState(null);
  const [showSavePresetModal, setShowSavePresetModal] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterKey, setFilterKey] = useState(0);

  useEffect(() => {
    if (isOpen) {
      loadFilterPresets();
      loadCurrentUser();
      loadUsers();
    }
  }, [isOpen]);

  const loadCurrentUser = async () => {
    try {
      const response = await apiService.getMe();
      setCurrentUser(response.user);
    } catch (error) {
      console.error("Error loading current user:", error);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await apiService.getCompanyUsers();
      setUsers(response.data || []);
    } catch (error) {
      console.error("Error loading users:", error);
    }
  };

  const loadFilterPresets = async () => {
    try {
      setLoading(true);
      const response = await apiService.getFilterPresets("opportunity");
      setPresets(response.data || []);
    } catch (error) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPreset = async (presetId) => {
    if (!presetId) {
      setSelectedPresetId(null);
      setFilterConfig({ logic: "AND", conditions: [] });
      setFilterKey((prev) => prev + 1);
      return;
    }

    try {
      const response = await apiService.getFilterPreset(presetId);
      const preset = response.data;
      setSelectedPresetId(presetId);

      const loadedConfig = preset.filterConfig || {
        logic: "AND",
        conditions: [],
      };

      if (Array.isArray(loadedConfig.conditions)) {
        loadedConfig.conditions = loadedConfig.conditions.filter(
          (condition) => {
            if (condition.type === "group") {
              condition.conditions = (condition.conditions || []).filter(
                (c) => c.field && c.operator,
              );
              return condition.conditions.length > 0;
            }
            return condition.field && condition.operator;
          },
        );
      } else {
        loadedConfig.conditions = [];
      }

      setFilterConfig(loadedConfig);
      setFilterKey((prev) => prev + 1);

      apiService.recordFilterPresetUsage(presetId).catch((err) => {
        console.error("Error recording usage:", err);
      });
    } catch (error) {
      console.error("Error loading preset:", error);
      alert("Failed to load filter preset");
    }
  };

  const handleDeletePreset = async (presetId) => {
    if (!window.confirm("Are you sure you want to delete this filter preset?"))
      return;

    try {
      await apiService.deleteFilterPreset(presetId);
      setPresets(presets.filter((p) => p.id !== presetId));
      if (selectedPresetId === presetId) {
        setSelectedPresetId(null);
        setFilterConfig({ logic: "AND", conditions: [] });
        setFilterKey((prev) => prev + 1);
      }
      alert("Filter preset deleted successfully!");
    } catch (error) {
      console.error("Error deleting preset:", error);
      alert("Failed to delete filter preset");
    }
  };

  const handleSetDefaultPreset = async (presetId) => {
    try {
      await apiService.updateFilterPreset(presetId, { isDefault: true });
      await loadFilterPresets();
      alert("Default filter preset updated!");
    } catch (error) {
      console.error("Error setting default preset:", error);
      alert("Failed to set default preset");
    }
  };

  const handleSavePreset = async (presetData) => {
    try {
      if (!filterConfig.conditions || filterConfig.conditions.length === 0) {
        alert("Cannot save empty filter. Please add at least one condition.");
        return;
      }
      const response = await apiService.createFilterPreset({
        ...presetData,
        entityType: "opportunity",
        filterConfig: filterConfig,
      });
      setPresets([...presets, response.data]);
      setShowSavePresetModal(false);
      alert("Filter preset saved successfully!");
    } catch (error) {
      console.error("Error saving preset:", error);
      throw error;
    }
  };

  const handleApplyFilters = (config) => setFilterConfig(config);

  const handleApplyAndClose = () => {
    onApplyFilters(filterConfig, selectedPresetId);
    onClose();
  };

  const handleClearFilters = () => {
    setFilterConfig({ logic: "AND", conditions: [] });
    setSelectedPresetId(null);
    setFilterKey((prev) => prev + 1);
  };

  const hasValidConditions = () => {
    if (!filterConfig?.conditions || !Array.isArray(filterConfig.conditions))
      return false;
    if (filterConfig.conditions.length === 0) return false;
    return filterConfig.conditions.some((condition) => {
      if (condition.type === "group") {
        return (
          condition.conditions &&
          Array.isArray(condition.conditions) &&
          condition.conditions.length > 0 &&
          condition.conditions.some((c) => c.field && c.operator)
        );
      }
      return condition.field && condition.operator;
    });
  };

  if (!isOpen) return null;

  const canApplyFilters = hasValidConditions();

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
        <div
          className={`w-full max-w-6xl max-h-[90vh] rounded-xl shadow-2xl flex flex-col overflow-hidden ${
            isDarkMode ? "bg-gray-900" : "bg-white"
          }`}
        >
          {/* Header */}
          <div
            className={`flex items-center justify-between p-6 border-b flex-shrink-0 ${
              isDarkMode ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <div className="flex items-center gap-3">
              <Filter size={24} className="text-blue-400" />
              <div>
                <h2
                  className={`text-2xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  Advanced Opportunity Filters
                </h2>
                <p
                  className={`text-sm mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  Build complex filters to find exactly what you need
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className={`p-2 rounded-lg transition-colors ${
                isDarkMode
                  ? "text-gray-400 hover:text-white hover:bg-gray-800"
                  : "text-gray-500 hover:text-gray-900 hover:bg-gray-100"
              }`}
            >
              <X size={24} />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* Filter Preset Selector */}
            <div className="space-y-2">
              <label
                className={`block text-sm font-medium ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Load Saved Filter
              </label>
              {loading ? (
                <div
                  className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  Loading presets...
                </div>
              ) : (
                <FilterPresetSelector
                  presets={presets}
                  selectedPresetId={selectedPresetId}
                  onSelectPreset={handleSelectPreset}
                  onDeletePreset={handleDeletePreset}
                  onSetDefault={handleSetDefaultPreset}
                  currentUserId={currentUser?.id}
                />
              )}
            </div>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div
                  className={`w-full border-t ${
                    isDarkMode ? "border-gray-700" : "border-gray-200"
                  }`}
                />
              </div>
              <div className="relative flex justify-center text-sm">
                <span
                  className={`px-4 ${
                    isDarkMode
                      ? "bg-gray-900 text-gray-400"
                      : "bg-white text-gray-500"
                  }`}
                >
                  {selectedPresetId
                    ? "Edit or modify selected filter"
                    : "Or build a new filter"}
                </span>
              </div>
            </div>

            {/* Filter Builder */}
            <AdvancedOpportunityFilterBuilder
              key={filterKey}
              onApply={handleApplyFilters}
              onSave={() => {
                if (
                  !filterConfig.conditions ||
                  filterConfig.conditions.length === 0
                ) {
                  alert(
                    "Please add at least one filter condition before saving",
                  );
                  return;
                }
                setShowSavePresetModal(true);
              }}
              initialFilters={filterConfig}
              showSaveButton={true}
              users={users}
            />
          </div>

          {/* Footer */}
          <div
            className={`flex items-center justify-between p-6 border-t flex-shrink-0 ${
              isDarkMode
                ? "border-gray-700 bg-gray-800/50"
                : "border-gray-200 bg-gray-50"
            }`}
          >
            <div className="flex items-center gap-2">
              <button
                onClick={handleClearFilters}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  isDarkMode
                    ? "text-gray-300 hover:text-white hover:bg-gray-700"
                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-200"
                }`}
              >
                Clear All
              </button>
              {filterConfig.conditions &&
                filterConfig.conditions.length > 0 && (
                  <div
                    className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    {filterConfig.conditions.length} condition
                    {filterConfig.conditions.length !== 1 ? "s" : ""} applied
                  </div>
                )}
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={onClose}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  isDarkMode
                    ? "text-gray-300 hover:text-white hover:bg-gray-700"
                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-200"
                }`}
              >
                Cancel
              </button>
              <button
                onClick={handleApplyAndClose}
                disabled={!canApplyFilters}
                className="flex items-center gap-2 px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors font-medium"
                title={
                  !canApplyFilters
                    ? "Add at least one valid filter condition"
                    : ""
                }
              >
                <Filter size={16} />
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      </div>

      <SaveFilterPresetModal
        isOpen={showSavePresetModal}
        onClose={() => setShowSavePresetModal(false)}
        onSave={handleSavePreset}
      />
    </>
  );
};

export default AdvancedOpportunityFilterModal;
